import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Lightbulb, ClipboardList, Sparkles, X } from 'lucide-react';

const STEPS = [
    {
        icon: Lightbulb,
        title: 'Save your ideas',
        body: "Paste a link or type a location — we'll tag the category and location for you.",
    },
    {
        icon: ClipboardList,
        title: 'Share your preferences',
        body: 'Everyone in your group submits their travel style, budget and interests before planning starts.',
    },
    {
        icon: Sparkles,
        title: 'Generate a fair itinerary',
        body: "Once the group votes on ideas, the AI builds a day-by-day plan that balances everyone's must-haves.",
    },
];

export default function OnboardingModal({ open, onClose }) {
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (open) setStep(0);
    }, [open]);

    if (!open) return null;

    const { icon: Icon, title, body } = STEPS[step];
    const isLast = step === STEPS.length - 1;

    const finish = () => onClose?.();
    const next = () => (isLast ? finish() : setStep((s) => s + 1));

    return (
        <div
            data-testid="onboarding-modal"
            aria-modal="true"
            role="dialog"
            aria-labelledby="onboarding-title"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(31, 42, 68, 0.55)' }}
        >
            <div
                className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
                style={{ background: '#F7F4EF', border: '1px solid rgba(0,0,0,0.06)' }}
            >
                {/* Header row: progress + skip */}
                <div className="flex items-center justify-between px-6 pt-5">
                    <span
                        data-testid="onboarding-progress"
                        className="text-[10px] uppercase tracking-[0.2em] font-medium"
                        style={{ color: '#1F2A44' }}
                    >
                        Step {step + 1} of {STEPS.length}
                    </span>
                    <button
                        type="button"
                        onClick={finish}
                        data-testid="onboarding-skip"
                        aria-label="Skip onboarding"
                        className="inline-flex items-center gap-1 text-xs text-[var(--ns-text3)] hover:text-[var(--ns-text)] transition"
                    >
                        Skip <X size={13} />
                    </button>
                </div>

                {/* Progress dots */}
                <div className="flex items-center gap-1.5 px-6 mt-3">
                    {STEPS.map((_, i) => (
                        <span
                            key={i}
                            className="h-1 flex-1 rounded-full transition"
                            style={{ background: i <= step ? '#C4552D' : 'rgba(31, 42, 68, 0.15)' }}
                        />
                    ))}
                </div>

                {/* Icon + copy */}
                <div className="px-8 pt-8 pb-6 text-center">
                    <span
                        className="w-16 h-16 rounded-3xl inline-flex items-center justify-center"
                        style={{ background: 'rgba(196, 85, 45, 0.14)' }}
                    >
                        <Icon size={28} style={{ color: '#C4552D' }} strokeWidth={1.75} />
                    </span>
                    <h2
                        id="onboarding-title"
                        data-testid={`onboarding-step-${step + 1}-title`}
                        className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-5"
                        style={{ color: '#1F2A44' }}
                    >
                        {title}
                    </h2>
                    <p
                        data-testid={`onboarding-step-${step + 1}-body`}
                        className="text-[15px] mt-3 leading-relaxed"
                        style={{ color: 'rgba(31, 42, 68, 0.78)' }}
                    >
                        {body}
                    </p>
                </div>

                {/* Footer buttons */}
                <div className="px-6 pb-6 flex items-center gap-3">
                    <Button
                        type="button"
                        onClick={finish}
                        variant="ghost"
                        data-testid="onboarding-skip-secondary"
                        className="rounded-full h-11 px-5 text-[var(--ns-text2)] hover:text-[var(--ns-text)]"
                    >
                        Skip
                    </Button>
                    <Button
                        type="button"
                        onClick={next}
                        data-testid={isLast ? 'onboarding-finish' : 'onboarding-next'}
                        className="rounded-full h-11 px-6 ml-auto text-white font-medium"
                        style={{ background: '#1F2A44' }}
                    >
                        {isLast ? 'Get started' : 'Next'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
